import React from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Alert from '@mui/material/Alert';
import { AlertTitle, Button, Checkbox, Box, CircularProgress, Link, Icon } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ApiParticipant } from '../../contexts/HttpClientContext';
import { CheckInState } from './EventModalTypes';
import { useTranslation } from 'react-i18next';

export const ParticipantsList = ({ participants }: { participants: ApiParticipant[] }) => {
    return <Stack>
        {participants.sort((a, b) => {
            if (a.name.toLocaleLowerCase() === "dold deltagare" && b.name.toLocaleLowerCase() !== "dold deltagare") return 1;
            if (a.name.toLocaleLowerCase() !== "dold deltagare" && b.name.toLocaleLowerCase() === "dold deltagare") return -1;

            return a.name > b.name ? 1 : -1;
        }).map(participant => {
            return (<Grid key={participant.id} container spacing={2} mb={1}>
                <Grid size={1}><Typography variant='body1'><PersonOutlinedIcon /></Typography></Grid>
                <Grid size={11}><Typography variant='body1'>{participant.name}</Typography></Grid>
            </Grid>);
        })}
    </Stack>
}

interface ParticipantCheckInProps {
    participants: ApiParticipant[];
    onCheckIn: (participant: ApiParticipant) => void;
    onCheckInPartner: (partnerParticipant: ApiParticipant, mainParticipant: ApiParticipant) => void;
    checkInState: { [membershipNumber: string]: CheckInState };
}

export const ParticipantsAccordion = (props: ParticipantCheckInProps) => {

    const { t } = useTranslation();

    const getCheckInStateStatus = (membershipNumber: string) => {
        if (!props.checkInState[membershipNumber]) {
            return "idle"
        }
        return props.checkInState[membershipNumber].status;
    }

    const getCheckInState = (membershipNumber: string) => {
        return props.checkInState[membershipNumber]
    }

    return (
        <div>
            {props.participants.sort((a, b) => a.name > b.name ? 1 : -1).map(participant => (
                <Accordion key={participant.id}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={"panel-content" + participant.id}
                        id={"panel-header-" + participant.id}
                    >
                        <PersonOutlinedIcon color={participant.checkedIn === 'yes' ? 'success' : 'primary'} />
                        <Typography component="span">
                            {participant.name}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {getCheckInStateStatus(participant.membershipNumber) === "error-checkIn" &&
                            <Alert variant='outlined' severity='error'>
                                <AlertTitle>Kunde inte checka in!</AlertTitle>
                                <p>Ett oväntat fel har inträffat vid incheckning.</p>
                            </Alert>
                        }
                        {getCheckInStateStatus(participant.membershipNumber) === "error-checkOut" &&
                            <Alert variant='outlined' severity='error'>
                                <AlertTitle>Kunde inte checka ut!</AlertTitle>
                                <p>Ett oväntat fel har inträffat vid utcheckning.</p>
                            </Alert>
                        }
                        {getCheckInStateStatus(participant.membershipNumber) === "success" &&
                            <Alert variant='outlined' severity='success'>{t("participants.status_checked_in")}</Alert>
                        }
                        {getCheckInStateStatus(participant.membershipNumber) === "removed" &&
                            <Alert variant='outlined' severity='info'>{t("participants.status_checked_out")}</Alert>
                        }
                        {getCheckInStateStatus(participant.membershipNumber) === "alreadyCheckedIn" &&
                            <Alert variant='outlined' severity='info'>{t("participants.status_already_checked_in")}</Alert>
                        }
                        {getCheckInStateStatus(participant.membershipNumber) === "partner-alreadyCheckedIn" &&
                            <Alert variant='outlined' severity='info'>{t("participants.status_partner_already_checked_in")}</Alert>
                        }
                        {getCheckInStateStatus(participant.membershipNumber) === "partner-error" &&
                            <Alert variant='outlined' severity='error'>
                                <AlertTitle>{t("participants.status_partner_error")}</AlertTitle>
                                <p>{t("participants.status_partner_error_desc")}</p>
                                <Button onClick={() => props.onCheckInPartner(getCheckInState(participant.membershipNumber).partnerParticipant!, participant)} variant='outlined'>{t("participants.try_again")}</Button>
                            </Alert>
                        }
                        {(getCheckInStateStatus(participant.membershipNumber) === "success-ask-partner" || getCheckInStateStatus(participant.membershipNumber) === "partner-checkIn-loading") &&
                            <Alert variant='outlined' severity='success'>
                                <AlertTitle>{t("participants.status_ask_partner")}</AlertTitle>
                                {getCheckInState(participant.membershipNumber)?.partnerParticipant &&
                                    <>
                                        <p>{t("participants.status_ask_partner_desc", { name: getCheckInState(participant.membershipNumber).partnerParticipant?.name })}</p>
                                        <p>Bokningsinformation: {getCheckInState(participant.membershipNumber).partnerParticipant?.bookingInfo}</p>
                                        {getCheckInStateStatus(participant.membershipNumber) === "partner-checkIn-loading" &&
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <CircularProgress size={'30px'} />
                                            </Box>
                                        }
                                        {getCheckInStateStatus(participant.membershipNumber) === "success-ask-partner" &&
                                            <Button onClick={() => props.onCheckInPartner(getCheckInState(participant.membershipNumber).partnerParticipant!, participant)} variant='outlined'>{t("participants.yes_check_in_partner")}</Button>
                                        }
                                    </>
                                }
                            </Alert>
                        }
                        {getCheckInStateStatus(participant.membershipNumber) === "partner-success" &&
                            <Alert variant='outlined' severity='success'>
                                <AlertTitle>{t("participants.partner_checked_in")}</AlertTitle>
                                <p>{getCheckInState(participant.membershipNumber)?.partnerParticipant?.name} är nu incheckad.</p>
                            </Alert>

                        }
                        <Grid container alignItems="center" spacing={1}>
                            <Grid size={2}>
                                <Typography component='p'>

                                    {getCheckInStateStatus(participant.membershipNumber) === "loading" &&
                                        <CircularProgress size={'1em'} />
                                    }
                                    {getCheckInStateStatus(participant.membershipNumber) !== "loading" &&
                                        <Checkbox
                                            checked={participant.checkedIn === "yes"}
                                            onChange={() => props.onCheckIn(participant)}
                                        />
                                    }
                                </Typography>
                            </Grid>
                            <Grid size={8}>
                                <Typography component="p">
                                    {participant.bookingInfo}
                                </Typography>
                            </Grid>
                            <Grid size={2} textAlign={'right'}>
                                <Typography component="p">
                                    <ExternalLink userKey={participant.userKey} />
                                </Typography>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            ))}
        </div>
    )
}

export const ExternalLink = ({ userKey }: { userKey: string }) => (
    <Link href={`https://minaaktiviteter.se/admin/addressbook/person/?person=${userKey}`} target="_blank" rel="noopener noreferrer">
        <Icon component={OpenInNewIcon} sx={{ ml: 0.5 }} />
    </Link>
);